import CloseIcon from '@mui/icons-material/Close';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { CropEmbeddingSearchResponse, ProductImage, ProductImageSource } from '../../../types';
import { transformToMaximumSize } from './ImageUpload';
import { ImageZoomModal } from './ImageZoomModal';
import { SelectEmbeddingView } from './SelectEmbeddingView';

type ModalView = 'manage' | 'select-embedding';

export interface PendingImage {
  imageUrl: string;
  isNew: boolean;
  productImageId?: number;
  blob?: Blob;
  source: string;
  origin?: string;
}

interface ProductImagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  primaryImage: ProductImage | null;
  alternativeImages: ProductImage[];
  shelfImages: ProductImage[];
  otherChainImages: ProductImage[];
  currentChainId: number;
  chainNamesMap: Record<number, string>;
  embeddings: CropEmbeddingSearchResponse[];
  pendingNewImage?: { image: PendingImage; targetType: 'primary' | 'alternative' };
  onSave: (changes: {
    primary: PendingImage | null;
    alternatives: PendingImage[];
  }) => Promise<void>;
}

export const ProductImagesModal: React.FC<ProductImagesModalProps> = ({
  isOpen,
  onClose,
  primaryImage,
  alternativeImages,
  shelfImages,
  otherChainImages,
  currentChainId,
  chainNamesMap,
  embeddings,
  pendingNewImage,
  onSave,
}) => {
  const { hasInternalAccess } = useAuthContext();
  const [view, setView] = useState<ModalView>('manage');
  const [selectTarget, setSelectTarget] = useState<'primary' | 'alternative'>('primary');
  const [saving, setSaving] = useState(false);
  const [loadingEmbeddingId, setLoadingEmbeddingId] = useState<string | null>(null);
  const [zoomImageSrc, setZoomImageSrc] = useState<string | null>(null);

  // Pending state for unsaved changes
  const [pendingPrimary, setPendingPrimary] = useState<PendingImage | null>(null);
  const [pendingAlternatives, setPendingAlternatives] = useState<PendingImage[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset pending state when modal opens or source data changes
  useEffect(() => {
    if (isOpen) {
      const basePrimary: PendingImage | null = primaryImage
        ? {
            imageUrl: primaryImage.imageUri,
            isNew: false,
            productImageId: primaryImage.productImageId,
            source: primaryImage.source,
          }
        : null;
      const baseAlternatives: PendingImage[] = alternativeImages.map((img) => ({
        imageUrl: img.imageUri,
        isNew: false,
        productImageId: img.productImageId,
        source: img.source,
      }));

      if (pendingNewImage) {
        if (pendingNewImage.targetType === 'primary') {
          // New image becomes primary; existing primary is demoted to front of alternatives
          setPendingPrimary(pendingNewImage.image);
          setPendingAlternatives(
            basePrimary ? [basePrimary, ...baseAlternatives] : baseAlternatives
          );
        } else {
          // New image appended as alternative
          setPendingPrimary(basePrimary);
          setPendingAlternatives([...baseAlternatives, pendingNewImage.image]);
        }
      } else {
        setPendingPrimary(basePrimary);
        setPendingAlternatives(baseAlternatives);
      }
      setView('manage');
      setHasChanges(!!pendingNewImage);
    }
  }, [isOpen, primaryImage, alternativeImages, pendingNewImage]);

  // Ref to track pendingPrimary for async callbacks (canvas.toBlob)
  // where the closure-captured value would be stale
  const pendingPrimaryRef = useRef(pendingPrimary);
  pendingPrimaryRef.current = pendingPrimary;

  const primaryFileInputRef = useRef<HTMLInputElement>(null);
  const alternativeFileInputRef = useRef<HTMLInputElement>(null);

  const fetchImageBlob = useCallback(async (url: string): Promise<Blob> => {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText} (${url})`);
    }
    return response.blob();
  }, []);

  const handleSelectFromEmbeddings = useCallback((target: 'primary' | 'alternative') => {
    setSelectTarget(target);
    setView('select-embedding');
  }, []);

  const handleEmbeddingSelected = useCallback(
    async (embedding: CropEmbeddingSearchResponse) => {
      setLoadingEmbeddingId(embedding.productEmbeddingCropId ?? null);
      try {
        const blob = await fetchImageBlob(embedding.vizUrl).catch(() => undefined);
        if (!blob) return;

        const newImage: PendingImage = {
          imageUrl: embedding.vizUrl,
          isNew: true,
          blob,
          source: ProductImageSource.Embedding,
          origin: 'control',
        };

        if (selectTarget === 'primary') {
          const demoted = pendingPrimary;
          setPendingPrimary(newImage);
          setPendingAlternatives((prev) => (demoted && !demoted.isNew ? [demoted, ...prev] : prev));
        } else {
          setPendingAlternatives((prev) => [...prev, newImage]);
        }
        setHasChanges(true);
        setView('manage');
      } finally {
        setLoadingEmbeddingId(null);
      }
    },
    [selectTarget, fetchImageBlob, pendingPrimary]
  );

  const handleFileUpload = useCallback((target: 'primary' | 'alternative') => {
    if (target === 'primary') {
      primaryFileInputRef.current?.click();
    } else {
      alternativeFileInputRef.current?.click();
    }
  }, []);

  const processFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, target: 'primary' | 'alternative') => {
      const file = e.target.files?.[0];
      if (!file) return;

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const [width, height] = transformToMaximumSize(
          img.naturalWidth,
          img.naturalHeight,
          500,
          500
        );
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg');
        canvas.toBlob((blob) => {
          const newImage: PendingImage = {
            imageUrl: dataUrl,
            isNew: true,
            blob: blob ?? undefined,
            source: ProductImageSource.UserUpload,
            origin: 'control',
          };

          if (target === 'primary') {
            const demoted = pendingPrimaryRef.current;
            setPendingPrimary(newImage);
            setPendingAlternatives((prev) =>
              demoted && !demoted.isNew ? [demoted, ...prev] : prev
            );
          } else {
            setPendingAlternatives((prev) => [...prev, newImage]);
          }
          setHasChanges(true);
        }, 'image/jpeg');
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
      };

      img.src = objectUrl;

      // Reset file input so the same file can be selected again
      e.target.value = '';
    },
    []
  );

  const handleDeleteImage = useCallback((index: number, isAlternative: boolean) => {
    if (isAlternative) {
      setPendingAlternatives((prev) => prev.filter((_, i) => i !== index));
    } else {
      setPendingAlternatives((prev) => {
        setPendingPrimary(prev[0] ?? null);
        return prev.slice(1);
      });
    }
    setHasChanges(true);
  }, []);

  const handleSetAsPrimary = useCallback(
    (index: number) => {
      const promoted = pendingAlternatives[index];
      const remainingAlts = pendingAlternatives.filter((_, i) => i !== index);
      const newAlts = pendingPrimary ? [...remainingAlts, pendingPrimary] : remainingAlts;
      setPendingPrimary(promoted);
      setPendingAlternatives(newAlts);
      setHasChanges(true);
    },
    [pendingAlternatives, pendingPrimary]
  );

  // Drag and drop reordering for alternative images
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((index: number) => {
    dragIndexRef.current = index;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((dropIndex: number) => {
    const dragIndex = dragIndexRef.current;
    if (dragIndex === null || dragIndex === dropIndex) {
      dragIndexRef.current = null;
      setDragOverIndex(null);
      return;
    }

    setPendingAlternatives((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, moved);
      return updated;
    });
    setHasChanges(true);
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave({
        primary: pendingPrimary,
        alternatives: pendingAlternatives,
      });
      onClose();
    } catch {
      // Error handling is done in the parent
    } finally {
      setSaving(false);
    }
  }, [onSave, pendingPrimary, pendingAlternatives, onClose]);

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const handleClose = useCallback(() => {
    if (hasChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  }, [hasChanges, onClose]);

  // Group other chain images by chainId
  const otherChainGroups = otherChainImages.reduce<Record<number, ProductImage[]>>((acc, img) => {
    if (!acc[img.chainId]) acc[img.chainId] = [];
    acc[img.chainId].push(img);
    return acc;
  }, {});

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold">Product Images</h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-full">
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {view === 'select-embedding' ? (
            <SelectEmbeddingView
              embeddings={embeddings.filter((e) => e.isActive)}
              targetType={selectTarget}
              loadingEmbeddingId={loadingEmbeddingId}
              onSelect={handleEmbeddingSelected}
              onBack={() => setView('manage')}
            />
          ) : (
            <div className="space-y-6">
              {/* Current chain section */}
              <div className="flex gap-6">
                {/* Primary Image Section */}
                <div className="w-1/3">
                  <div className="flex items-center gap-2 mb-3">
                    <StarOutlineIcon sx={{ fontSize: 18, color: '#D97706' }} />
                    <h3 className="font-semibold">Primary Image</h3>
                  </div>

                  {/* Primary image slot */}
                  <div className="relative border border-gray-300 rounded-lg aspect-square mb-3 flex items-center justify-center bg-white overflow-hidden group">
                    {pendingPrimary ? (
                      <>
                        {/* Primary badge */}
                        <span className="absolute top-2 left-2 z-10 px-2 py-0.5 text-xs font-medium rounded border border-green-600 text-green-600 bg-white">
                          Primary
                        </span>
                        <button
                          className="absolute top-2 right-2 z-10 p-1 bg-white bg-opacity-75 rounded-md shadow hover:bg-opacity-100"
                          onClick={() => setZoomImageSrc(pendingPrimary.imageUrl)}
                        >
                          <ZoomOutMapIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                        </button>
                        <img
                          src={pendingPrimary.imageUrl}
                          alt="Primary product"
                          className="w-full h-full object-contain"
                        />
                        {/* Hover actions */}
                        <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-20">
                          <button
                            onClick={() => handleDeleteImage(0, false)}
                            disabled={
                              (pendingPrimary?.origin !== 'control' && !hasInternalAccess) ||
                              pendingAlternatives.length === 0
                            }
                            className="p-2 bg-white rounded-full shadow hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={
                              pendingPrimary?.origin !== 'control' && !hasInternalAccess
                                ? 'This image cannot be removed'
                                : pendingAlternatives.length === 0
                                  ? 'Cannot delete the only image'
                                  : 'Delete'
                            }
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 20, color: '#EF4444' }} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-300 text-center">
                        <CollectionsOutlinedIcon sx={{ fontSize: 48 }} />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleSelectFromEmbeddings('primary')}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm mb-2"
                  >
                    <CollectionsOutlinedIcon sx={{ fontSize: 16 }} />
                    Select from embeddings
                  </button>
                  <button
                    onClick={() => handleFileUpload('primary')}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                  >
                    <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    Upload file
                  </button>
                </div>

                {/* Alternative Images Section */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <CollectionsOutlinedIcon sx={{ fontSize: 18, color: '#7C3AED' }} />
                    <h3 className="font-semibold">Alternative Images</h3>
                  </div>

                  {/* Alternative image slots */}
                  <div className="max-h-[358px] overflow-y-auto mb-3">
                    <div className="grid grid-cols-3 gap-3">
                      {pendingAlternatives.length > 0 ? (
                        pendingAlternatives.map((image, i) => (
                          <div
                            key={`${image.imageUrl}-${i}`}
                            draggable
                            onDragStart={() => handleDragStart(i)}
                            onDragOver={(e) => handleDragOver(e, i)}
                            onDrop={() => handleDrop(i)}
                            onDragEnd={handleDragEnd}
                            className={`relative border rounded-lg aspect-square flex items-center justify-center bg-white overflow-hidden group cursor-grab active:cursor-grabbing ${
                              dragOverIndex === i ? 'border-purple-500 border-2' : 'border-gray-300'
                            }`}
                          >
                            <span className="absolute top-2 left-2 z-10 px-2 py-0.5 text-xs font-medium rounded border border-purple-600 text-purple-600 bg-white">
                              Alternative
                            </span>
                            <button
                              className="absolute top-2 right-2 z-10 p-1 bg-white bg-opacity-75 rounded-md shadow hover:bg-opacity-100"
                              onClick={() => setZoomImageSrc(image.imageUrl)}
                            >
                              <ZoomOutMapIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                            </button>
                            <img
                              src={image.imageUrl}
                              alt={`Alternative ${i + 1}`}
                              className="w-full h-full object-contain"
                            />
                            <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-20">
                              <button
                                onClick={() => handleSetAsPrimary(i)}
                                className="p-2 bg-white rounded-full shadow hover:bg-yellow-50"
                                title="Set as primary"
                              >
                                <StarOutlineIcon sx={{ fontSize: 20, color: '#D97706' }} />
                              </button>
                              <button
                                onClick={() => handleDeleteImage(i, true)}
                                disabled={image.origin !== 'control' && !hasInternalAccess}
                                className="p-2 bg-white rounded-full shadow hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                title={
                                  image.origin !== 'control' && !hasInternalAccess
                                    ? 'This image cannot be removed'
                                    : 'Delete'
                                }
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 20, color: '#EF4444' }} />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="border border-gray-300 rounded-lg aspect-square flex items-center justify-center bg-white">
                          <div className="text-gray-300">
                            <CollectionsOutlinedIcon sx={{ fontSize: 36 }} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => handleSelectFromEmbeddings('alternative')}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm mb-2"
                  >
                    <CollectionsOutlinedIcon sx={{ fontSize: 16 }} />
                    Select from embeddings
                  </button>
                  <button
                    onClick={() => handleFileUpload('alternative')}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                  >
                    <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    Upload file
                  </button>
                </div>
              </div>

              {/* Shelf images (read-only) */}
              {shelfImages.length > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <LockOutlinedIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                    <h3 className="font-semibold text-gray-500">Shelf</h3>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                      Read-only
                    </span>
                  </div>
                  <div className="flex gap-4">
                    {shelfImages.map((img, i) => (
                      <div
                        key={`shelf-${i}`}
                        className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center"
                      >
                        <img
                          src={img.imageUri}
                          alt="Shelf"
                          className="max-h-[200px] w-auto object-contain opacity-75"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other chain images */}
              {Object.entries(otherChainGroups).map(([chainIdStr, imgs]) => {
                const chainId = Number(chainIdStr);
                const chainName =
                  chainNamesMap[chainId] || (chainId ? `Chain ${chainId}` : 'No chain');
                return (
                  <div key={chainId} className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <LockOutlinedIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                      <h3 className="font-semibold text-gray-500">{chainName}</h3>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        Read-only
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      {imgs
                        .sort((a, b) => a.position - b.position)
                        .map((img) => (
                          <div
                            key={img.productImageId}
                            className="relative border border-gray-200 rounded-lg aspect-square flex items-center justify-center bg-gray-50 overflow-hidden"
                          >
                            <img
                              src={img.imageUri}
                              alt={chainName}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {view === 'manage' && (
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <button
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !pendingPrimary}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}

        {/* Hidden file inputs */}
        <input
          ref={primaryFileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => processFileUpload(e, 'primary')}
        />
        <input
          ref={alternativeFileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => processFileUpload(e, 'alternative')}
        />
      </div>

      {/* Discard changes confirmation */}
      {showDiscardConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold mb-2">Discard changes?</h3>
            <p className="text-sm text-gray-600 mb-6">
              You have unsaved changes that will be lost.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDiscardConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium"
              >
                Keep editing
              </button>
              <button
                onClick={() => {
                  setShowDiscardConfirm(false);
                  onClose();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal (rendered above the product images modal) */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={(e) => e.stopPropagation()}>
        <ImageZoomModal
          isOpen={!!zoomImageSrc}
          imageSrc={zoomImageSrc || ''}
          onClose={() => setZoomImageSrc(null)}
        />
      </div>
    </div>
  );
};
